// src/components/admin/ToolIconField.jsx
//
// Choosing how a tool looks in the catalog: its own logo, or one of the
// curated glyphs.
//
// The image uploads on its own rather than through the form's submit, like the
// installer uploader — and for the same reason. The glyph, by contrast, is a
// plain string on the tool document, so it rides along with Save like every
// other field. That split is why this component takes both a `value`/`onChange`
// pair (glyph, owned by the form) and a tool id (image, owned by the server).

import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ImageUp, Trash2 } from 'lucide-react';
import { useTools } from '../../hooks/useTools';
import { hasCustomIcon } from '../../services/toolIcons';
import IconPicker from '../common/IconPicker';
import ToolIcon from '../common/ToolIcon';
import { Button, Field, IconButton, cx } from '../common/ui';

const describeFailure = (err) => {
  const said = err.response?.data?.error;
  if (said) return said;
  if (!err.response) {
    return 'The upload never reached the server. Check that the API is reachable from this site.';
  }
  if (err.response.status === 413) {
    return 'A proxy refused the image as too large — raise client_max_body_size (nginx).';
  }
  return `The server rejected the image (HTTP ${err.response.status}).`;
};

const ToolIconField = ({ tool, value, onChange }) => {
  const { uploadToolIcon, removeToolIcon } = useTools(true);
  // The form's `tool` is a snapshot from when the modal opened, so the uploaded
  // image is tracked here from each response.
  const [current, setCurrent] = useState(tool);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const isNew = !tool?._id;
  const custom = hasCustomIcon(current);

  const choose = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBusy(true);
    try {
      const updated = await uploadToolIcon({ toolId: tool._id, file });
      setCurrent(updated);
      toast.success('Icon updated');
    } catch (err) {
      toast.error(describeFailure(err), { duration: 8000 });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const updated = await removeToolIcon({ toolId: tool._id });
      setCurrent(updated);
      toast.success('Icon removed — back to the glyph');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove that icon');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field
      label="Icon"
      hint={
        custom
          ? 'The catalog shows this logo. Remove it to go back to a glyph.'
          : 'Pick a glyph, or upload the product’s own logo.'
      }
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-3 rounded-lg border border-line bg-surface-2 p-3">
          {/* Exactly how the catalog will render it — untinted for a logo. */}
          <span
            className="cat-tile h-11 w-11 flex-none"
            data-color={custom ? 'logo' : 'indigo'}
          >
            <ToolIcon
              tool={custom ? current : undefined}
              name={value}
              fill
              className="h-6 w-6"
            />
          </span>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium">
              {custom ? current.iconImage.fileName : value}
            </p>
            <p className="mt-0.5 text-xs text-ink-3">
              {custom ? 'Uploaded logo' : 'Lucide glyph'}
            </p>
          </div>

          {custom && (
            <IconButton label="Remove the uploaded icon" tone="danger" onClick={remove} disabled={busy}>
              <Trash2 className="h-4 w-4" />
            </IconButton>
          )}
        </div>

        {isNew ? (
          <p className="text-xs text-ink-3">
            Want the product’s own logo here? Add the tool first — the uploader appears once it
            exists.
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={choose}
              disabled={busy}
              className="hidden"
            />
            <Button onClick={() => inputRef.current?.click()} disabled={busy}>
              <ImageUp className="h-4 w-4" />
              {busy ? 'Uploading…' : custom ? 'Replace logo' : 'Upload a logo'}
            </Button>
            <span className="text-xs text-ink-3">PNG, JPEG or WebP · up to 2 MB</span>
          </div>
        )}

        {/* Still offered while a logo is set: removing the image falls back to
            whatever glyph is chosen here, so it should never be left stale. */}
        <div className={cx(custom && 'opacity-60')}>
          <IconPicker value={value} onChange={onChange} />
        </div>
      </div>
    </Field>
  );
};

export default ToolIconField;
